import { MessageSquare } from "lucide-react"
import type * as React from "react"

import { cn } from "@/lib/utils"

import {
  REPORT_SEVERITY_ACTION_LABEL,
  type ReportFindingItem,
  type ReportSeverity,
} from "./report-data"

/**
 * The pieces every view of a finding shares — the number, the severity pill,
 * the section line and the round actions — so the checklist card and the list
 * row read as one report.
 */

/** Severity leads the card, so the pill carries its colour on its own: red, amber, violet. */
const SEVERITY_PILL: Record<ReportSeverity, string> = {
  error: "border-err/30 bg-err-tint text-err",
  warn: "border-print/30 bg-print-tint text-print",
  needs_sme: "border-people/30 bg-people-tint text-people",
}

/** The finding's number. `settled` turns it green: acknowledged, or corrected. */
export function FindingNumber({
  number,
  settled = false,
}: {
  number: number
  settled?: boolean
}): JSX.Element {
  return (
    <span
      className={cn(
        "grid size-7 shrink-0 place-items-center rounded-full text-body-sm font-bold tabular-nums text-white",
        settled ? "bg-master" : "bg-brand",
      )}
    >
      {number}
    </span>
  )
}

export function SeverityPill({ severity }: { severity: ReportSeverity }): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-1 text-[10.5px] font-medium uppercase leading-none tracking-[0.08em]",
        SEVERITY_PILL[severity],
      )}
    >
      {REPORT_SEVERITY_ACTION_LABEL[severity]}
    </span>
  )
}

/**
 * Where on the label, then which scorecard group, then anything the view adds:
 * secondary context, so one quiet line rather than more chips. Each part keeps
 * its own separator, so a wrap never leaves a dot hanging at a line end.
 */
export function FindingContext({
  finding,
  trailing,
}: {
  finding: Pick<ReportFindingItem, "sectionTitle" | "groupNumber" | "groupName">
  /** A last part — the list row's status. */
  trailing?: string
}): JSX.Element | null {
  const parts: React.ReactNode[] = []
  if (finding.sectionTitle) {
    parts.push(
      <span key="section" className="font-medium text-text-2">
        {finding.sectionTitle}
      </span>,
    )
  }
  if (finding.groupNumber != null) {
    parts.push(
      <span key="group" className="whitespace-nowrap">
        Group {finding.groupNumber}
        {finding.groupName ? ` — ${finding.groupName}` : ""}
      </span>,
    )
  }
  if (trailing) {
    parts.push(
      <span key="trailing" className="whitespace-nowrap font-medium text-text-2">
        {trailing}
      </span>,
    )
  }
  if (parts.length === 0) return null
  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-caption text-text-3">
      {parts.map((part, index) => (
        <span key={index} className="inline-flex items-baseline gap-x-1.5">
          {index > 0 && <span aria-hidden="true">·</span>}
          {part}
        </span>
      ))}
    </p>
  )
}

export function CircleButton({
  onClick,
  pressed,
  expanded,
  label,
  className,
  children,
}: {
  onClick: () => void
  pressed?: boolean
  /** For a button that opens a region in place, such as the list row's thread. */
  expanded?: boolean | undefined
  label: string
  className: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      className={cn(
        "grid size-8 shrink-0 place-items-center rounded-full border-2 transition-colors duration-120",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * The conversation button, with its unread count.
 *
 * A reader who may not post (a viewer) still opens the thread, read-only, but
 * gets no badge: unread is cleared by marking read, a write the server refuses
 * them, so a badge they can never clear would only nag.
 */
export function MessageButton({
  onClick,
  canPost,
  unread,
  open,
  expanded,
}: {
  onClick: () => void
  canPost: boolean
  unread: number
  /** The thread is showing: the button reads as on. */
  open?: boolean
  /** Set by a button whose thread opens in place, so assistive tech hears it open. */
  expanded?: boolean | undefined
}): JSX.Element {
  const showUnread = canPost && unread > 0
  const label = !canPost
    ? "Read the conversation about this finding"
    : showUnread
      ? `Message the review team about this finding, ${String(unread)} unread`
      : "Message the review team about this finding"
  return (
    <span className="relative">
      <CircleButton
        onClick={onClick}
        expanded={expanded}
        label={label}
        className={
          open
            ? "border border-brand bg-brand text-white"
            : "border border-border bg-surface text-text-3 hover:text-text"
        }
      >
        <MessageSquare className="size-4" aria-hidden="true" />
      </CircleButton>
      {showUnread && (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full border-2 border-surface bg-err px-1 text-[9px] font-semibold leading-3 text-white"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </span>
  )
}
